import React, { useEffect, useId, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { StudentReviewAuthor, studentReviewDate } from "../../../shared/reviews/StudentReviewPresentation";
import { listHomepageReviews } from "./homepageReviewApi";
import HomepageReviewHeading from "./HomepageReviewHeading";
import "../../../shared/styles/CardSlideForm.css";
import "./HomepageReviews.css";

// Public route and bounded course catalog are integrated for REVIEW-1.POST-2.
const PUBLIC_REVIEW_LIST_AVAILABLE = true;
// Local display watchdog, slightly beyond the Firebase SDK's 70s default.
// It never retries or changes the callable contract.
export const HOMEPAGE_REVIEW_LOADING_TIMEOUT_MS = 75000;

function ReviewCard({ review, onOpen }) {
  const text = useRef(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const element = text.current;
    let active = true;
    const measure = () => { if (active && element) setOverflows(element.scrollHeight > element.clientHeight + 1); };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (element) observer?.observe(element);
    window.addEventListener("resize", measure);
    document.fonts?.ready?.then(measure);
    document.fonts?.addEventListener?.("loadingdone", measure);
    return () => { active = false; observer?.disconnect(); window.removeEventListener("resize", measure); document.fonts?.removeEventListener?.("loadingdone", measure); };
  }, [review.reviewText]);
  return <article className="homepage-review-card">
    <div className="homepage-review-author"><StudentReviewAuthor review={review} maskPublicName /></div>
    <div className="homepage-review-date">{studentReviewDate(review.createdAt)}</div>
    <p className="homepage-review-text" ref={text}>{review.reviewText}</p>
    <div className="homepage-review-action">
      {overflows && <Button variant="link" className="p-0 text-decoration-none" aria-haspopup="dialog" onClick={() => onOpen(review)}>แสดงเพิ่มเติม</Button>}
    </div>
    <div className="homepage-review-course" title={review.courseName}>{review.courseName}</div>
  </article>;
}

export default function HomepageReviews({ courseId }) {
  // No configured course ID: the server discovers eligible courses within its budget.
  return <ReviewSection key={courseId ?? "all-enabled"} courseId={courseId} />;
}

function ReviewSection({ courseId }) {
  const headingId = useId(), sliderId = useId(), modalId = useId();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bounds, setBounds] = useState({ left: false, right: false });
  const slider = useRef(null);

  useEffect(() => {
    let current = true;
    const timeout = window.setTimeout(() => {
      if (current) { current = false; setLoading(false); }
    }, HOMEPAGE_REVIEW_LOADING_TIMEOUT_MS);
    // One bounded page only. Homepage pagination is intentionally disabled; the
    // public listing page consumes nextCursor on explicit user action.
    Promise.resolve().then(() => listHomepageReviews(courseId)).then((result) => {
      if (current) setReviews(result.reviews);
    }).catch(() => { /* Rollout OFF/errors never block other homepage content. */ })
      .finally(() => { window.clearTimeout(timeout); if (current) setLoading(false); });
    return () => { current = false; window.clearTimeout(timeout); };
  }, [courseId]);

  useEffect(() => {
    const element = slider.current;
    if (!element) return;
    const update = () => setBounds({ left: element.scrollLeft > 1, right: element.scrollLeft + element.clientWidth < element.scrollWidth - 1 });
    update();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    observer?.observe(element);
    element.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); element.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [reviews.length, loading]);

  const scroll = (direction) => {
    const element = slider.current;
    if (!element) return;
    const cardWidth = element.firstElementChild?.getBoundingClientRect().width || 0;
    const gap = parseFloat(window.getComputedStyle(element).columnGap) || 0;
    const step = cardWidth + gap;
    if (!cardWidth) return;
    // Move to the adjacent card boundary, also from a partially swiped position.
    // The final stop is clamped so the last card is fully visible without blank space.
    const index = direction > 0 ? Math.floor((element.scrollLeft + 1) / step) + 1 : Math.ceil((element.scrollLeft - 1) / step) - 1;
    const left = Math.max(0, Math.min(index * step, element.scrollWidth - element.clientWidth));
    element.scrollTo({ left, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  if (loading) return <section className="homepage-reviews" aria-labelledby={headingId} aria-busy="true">
    <HomepageReviewHeading id={headingId} listingAvailable={PUBLIC_REVIEW_LIST_AVAILABLE} />
    <div className="homepage-reviews-loading d-flex align-items-center justify-content-center">
      <Spinner animation="border" role="status"><span className="visually-hidden">กำลังโหลดรีวิว...</span></Spinner>
    </div>
  </section>;
  if (!reviews.length) return null;
  return <section className="homepage-reviews" aria-labelledby={headingId}>
    <HomepageReviewHeading id={headingId} listingAvailable={PUBLIC_REVIEW_LIST_AVAILABLE} />
    <div className="homepage-review-slider">
      <button type="button" className="slide-arrow slide-arrow-left homepage-review-arrow" aria-label="รีวิวก่อนหน้า" aria-controls={sliderId} disabled={!bounds.left} onClick={() => scroll(-1)}><BsChevronLeft size={34} /></button>
    <div id={sliderId} className="homepage-review-track" ref={slider} tabIndex={0} role="region" aria-label="เลื่อนรีวิวจากผู้เรียน"
      onKeyDown={(event) => { if (event.target === event.currentTarget && ["ArrowLeft", "ArrowRight"].includes(event.key)) { event.preventDefault(); scroll(event.key === "ArrowLeft" ? -1 : 1); } }}>
      {reviews.map((review) => <ReviewCard key={review.reviewId} review={review} onOpen={setSelected} />)}
    </div>
      <button type="button" className="slide-arrow slide-arrow-right homepage-review-arrow" aria-label="รีวิวถัดไป" aria-controls={sliderId} disabled={!bounds.right} onClick={() => scroll(1)}><BsChevronRight size={34} /></button>
    </div>
    <Modal show={Boolean(selected)} onHide={() => setSelected(null)} scrollable centered aria-labelledby={modalId} restoreFocusOptions={{ preventScroll: true }}>
      <Modal.Header closeButton closeLabel="ปิด"><Modal.Title id={modalId}>รีวิวจากผู้เรียน</Modal.Title></Modal.Header>
      <Modal.Body className="homepage-review-modal">
        {selected && <><StudentReviewAuthor review={selected} maskPublicName /><div className="homepage-review-date my-2">{studentReviewDate(selected.createdAt)}</div>
          <p className="homepage-review-full-text">{selected.reviewText}</p><div className="homepage-review-course-full">{selected.courseName}</div></>}
      </Modal.Body>
    </Modal>
  </section>;
}
